import { useRef, useState, useEffect, useMemo, Suspense } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Float, Text, Image as DreiImage } from '@react-three/drei';
import * as THREE from 'three';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Product } from '../types';
import { useExperienceStore } from '../state/useExperienceStore';

const SafeImage = ({ url, fallbackUrl, boxScale = [1, 1], ...props }: any) => {
  const [validUrl, setValidUrl] = useState<string | null>(null);
  const [aspect, setAspect] = useState(1);

  useEffect(() => {
    let isMounted = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => {
      if (isMounted) {
        setAspect(img.width / img.height);
        setValidUrl(url);
      }
    };
    img.onerror = () => {
      if (isMounted) {
        console.warn(`SafeImage: Failed to load ${url}, using fallback.`);
        const fallback = fallbackUrl || `https://picsum.photos/seed/fallback/600/800`;
        const fImg = new Image();
        fImg.crossOrigin = "anonymous";
        fImg.src = fallback;
        fImg.onload = () => {
          if (isMounted) {
            setAspect(fImg.width / fImg.height);
            setValidUrl(fallback);
          }
        };
        fImg.onerror = () => {
          if (isMounted) setValidUrl(fallback);
        };
      }
    };
    return () => { isMounted = false; };
  }, [url, fallbackUrl]);

  // Calculate fit scale within box
  const [boxW, boxH] = boxScale;
  const boxAspect = boxW / boxH;
  let finalScale: [number, number] = [boxW, boxH];
  
  if (aspect > boxAspect) {
    finalScale = [boxW, boxW / aspect];
  } else {
    finalScale = [boxH * aspect, boxH];
  }

  if (!validUrl) {
    return (
      <mesh {...props} scale={[...finalScale, 1]}>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial color="#111" transparent opacity={0.2} />
      </mesh>
    );
  }

  return (
    <Suspense fallback={null}>
      <DreiImage {...props} url={validUrl} scale={finalScale} />
    </Suspense>
  );
};

const ProductItem = ({ product, position, index }: { product: Product; position: [number, number, number]; index: number }) => {
  const selectProduct = useExperienceStore((state) => state.selectProduct);
  const [hovered, setHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const groupRef = useRef<THREE.Group>(null!);
  
  const frustum = useMemo(() => new THREE.Frustum(), []);
  const projScreenMatrix = useMemo(() => new THREE.Matrix4(), []);

  const worldPos = useMemo(() => new THREE.Vector3(), []);
  const viewport = useThree((state) => state.viewport);

  const isMobile = viewport.width < 10;
  const responsiveScale = isMobile ? 0.7 : 1;

  useFrame(({ camera, clock }) => {
    if (!groupRef.current) return;

    projScreenMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(projScreenMatrix);

    groupRef.current.getWorldPosition(worldPos);
    const sphere = new THREE.Sphere(worldPos, 3);
    const currentlyInView = frustum.intersectsSphere(sphere);

    if (currentlyInView !== isVisible) {
      setIsVisible(currentlyInView);
    }

    if (hovered && isVisible) {
      const t = clock.getElapsedTime();
      groupRef.current.rotation.y = Math.sin(t * 0.5) * 0.1;
      groupRef.current.rotation.x = Math.cos(t * 0.5) * 0.05;
    } else {
      groupRef.current.rotation.y = THREE.MathUtils.lerp(groupRef.current.rotation.y, 0, 0.05);
      groupRef.current.rotation.x = THREE.MathUtils.lerp(groupRef.current.rotation.x, 0, 0.05);
    }
  });

  return (
    <group ref={groupRef} position={position} scale={responsiveScale}>
      {isVisible && (
        <Float speed={1.5} rotationIntensity={0.1} floatIntensity={0.3}>
          <mesh 
            onPointerOver={() => setHovered(true)} 
            onPointerOut={() => setHovered(false)}
            onClick={(e) => {
              e.stopPropagation();
              selectProduct(product.id);
            }}
          >
            {/* Glass Background */}
            <planeGeometry args={[4.2, 6.2]} />
            <meshPhysicalMaterial 
              color="#ffffff"
              transparent
              opacity={hovered ? 0.1 : 0.04}
              roughness={0.05}
              metalness={0.9}
              transmission={0.4}
              thickness={0.5}
            />
            
            <group position={[0, 0.2, 0.1]}>
              <SafeImage
                url={product.images?.[0] || `https://picsum.photos/seed/${product.id}/600/900`}
                fallbackUrl={`https://picsum.photos/seed/${product.id}/600/900`}
                boxScale={[4, 5.5]}
                transparent
                opacity={hovered ? 1 : 0.75}
                toneMapped={false}
              />
            </group>

            {/* Glowing Neon Border */}
            <mesh position={[0, 0, -0.05]}>
              <planeGeometry args={[4.3, 6.3]} />
              <meshBasicMaterial 
                color="#ffffff" 
                transparent 
                opacity={hovered ? 0.35 : 0.08}
                blending={THREE.AdditiveBlending}
              />
            </mesh>

            {/* Neon Edge Frame */}
            <lineSegments position={[0, 0, 0]}>
              <edgesGeometry args={[new THREE.PlaneGeometry(4.2, 6.2)]} />
              <lineBasicMaterial 
                color={hovered ? "#ffffff" : "#333333"} 
                transparent 
                opacity={hovered ? 1 : 0.4} 
              />
            </lineSegments>
          </mesh>

          {/* Typography */}
          <group position={[-1.9, -3.9, 0.2]}>
             <Text
              fontSize={0.25}
              color="white"
              anchorX="left"
              fontWeight="bold"
              fillOpacity={hovered ? 0.8 : 0.4}
              letterSpacing={0.1}
            >
              {(index + 1).toString().padStart(2, '0')}
            </Text>
            <Text
              position={[0, -0.45, 0]}
              fontSize={0.4}
              color="white"
              fontWeight="black"
              font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfAZ9hjp-Ek-_EeA.woff"
              anchorX="left"
              letterSpacing={-0.01}
              fillOpacity={hovered ? 1 : 0.7}
            >
              {product.name.toUpperCase()}
            </Text>
            <Text
              position={[0, -0.9, 0]}
              fontSize={0.25}
              color="white"
              fontWeight="bold"
              anchorX="left"
              fillOpacity={0.6}
            >
              ${product.price?.toLocaleString()}
            </Text>
          </group>
        </Float>
      )}
    </group>
  );
};

export const StoreScene = () => {
  const { mode, selectedShopId } = useExperienceStore();
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if ((mode === 'STORE' || mode === 'PRODUCT') && selectedShopId) {
      const fetchProducts = async () => {
        try {
          const q = query(collection(db, "products"), where("shopId", "==", selectedShopId), limit(20));
          const snap = await getDocs(q);
          setProducts(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Product));
        } catch (e) {
          console.error("Error fetching products for 3D Store:", e);
        }
      };
      fetchProducts();
    }
  }, [mode, selectedShopId]);

  if (mode !== 'STORE' && mode !== 'PRODUCT') return null;

  return (
    <group>
      {/* Background */}
      <mesh position={[0, 0, -30]}>
        <planeGeometry args={[200, 200]} />
        <meshBasicMaterial color="#050505" />
      </mesh>

      {/* Decorative large header for shop context */}
      <Text
        position={[0, 15, -20]}
        fontSize={8}
        color="white"
        fillOpacity={0.01}
        fontWeight="black"
        letterSpacing={-0.05}
      >
        COLLECTION
      </Text>

      {/* Editorial gallery of products */}
      {products.map((product, i) => {
        const spacingX = 8;
        const spacingY = 10;
        
        const col = i % 2;
        const row = Math.floor(i / 2);
        
        const x = (col - 0.5) * spacingX + (Math.sin(i) * 1);
        const y = row * -spacingY + (Math.cos(i * 0.5) * 2);
        const z = -10 + Math.sin(i * 1.5) * 3;
        
        return (
          <ProductItem 
            key={product.id} 
            product={product} 
            position={[x, y, z]} 
            index={i} 
          />
        );
      })}
    </group>
  );
};
